import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import useDeleteParams from '../../hooks/useDeleteParams';
import useAuth from '../../hooks/useAuth';
import { authenticationService, eventService } from '../../services';
import { PersonalEvent as Event, EventParticipationAnswer } from '../../types/event';
import { CustomFieldResponses, InviteType } from '../../types/invites';
import { getUserId } from '../../services/httpService';
import { getPollResponses, userHasGivenAvailability } from '../../utils/eventUtils';
import { formatCustomFieldResponsesFromCustomFields } from '../../utils/formatUtils';
import { LoginPageState, SuccessPageState } from '../../types/navigation';
import ResponsePageWrapper from '../../components/layouts/ResponsePageWrapper';
import EventOverview from '../../components/overviews/EventOverview';
import CustomFieldsForm from '../../components/forms/CustomFieldsForm';
import Button from '../../components/misc/Button';
import EventSelectDateOption from '../../components/forms/events/EventSelectDateOption';
import { checkInviteEmailMismatch } from '../../utils/inviteUtils';

function AuthenticatedDatePickerRespondPage(): JSX.Element {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const auth = useAuth();

  useDeleteParams('link');

  const linkId = params.get('id');

  const [event, setEvent] = useState<Event>();
  const [customFieldResponses, setCustomFieldResponses] = useState<CustomFieldResponses>([]);
  const [hasGivenAvailability, setHasGivenAvailability] = useState<boolean>(false);
  const [dateOptionsUpdate, setDateOptionsUpdate] = useState<
    Record<number, { answer: EventParticipationAnswer; description?: string }>
  >({});

  useEffect(() => {
    eventService
      .getEventInviteWithToken(linkId as string)
      .then((e) => setEvent(e))
      .catch(() => {
        toast.error(t('toast.error.general.inviteNotFound'));
        navigate('/');
      });
  }, []);

  useEffect(() => {
    try {
      const userId = getUserId();
      if (!userId || !event) return;

      if (!event.isDatePicker) {
        navigate(`/event/invite?id=${event.id}`);
      }

      const responses = getPollResponses(userId, event!.customFields);
      setCustomFieldResponses(formatCustomFieldResponsesFromCustomFields(event!.customFields, responses));
      setHasGivenAvailability(userHasGivenAvailability(event, userId));
    } catch (error) {
      console.error(error);
    }
  }, [event]);

  const validate = () => {
    if (!hasGivenAvailability && Object.keys(dateOptionsUpdate).length !== event!.dateOptions.length) {
      toast.error(t('page.events.invite.datePickers.missingOptions'));
      return false;
    }

    if (event!.customFields.length > 0) {
      return event!.customFields.every((field) => {
        if (field.mandatory && !customFieldResponses[field.customFieldId]) {
          toast.error(t('toast.error.field.mandatoryStar'));
          return false;
        }
        return true;
      });
    }

    return true;
  };

  const handleSubmit = async (): Promise<void> => {
    if (!validate() || !event?.id) return;

    const dateOptions = Object.entries(dateOptionsUpdate).map(([dateOptionId, { answer, description }]) => ({
      dateOptionId: parseInt(dateOptionId, 10),
      answer,
      description,
    }));

    await eventService.respondToEventDateOptionBulk(`${event.id}`, dateOptions);

    if (event!.customFields.length > 0) {
      await eventService.respondToEventPollBulk(`${event.id}`, customFieldResponses);
    }

    const state: SuccessPageState = { translationKey: 'page.events.invite.message.success' };
    navigate('/success', { state });
  };

  if (!event) return <></>;

  const emailCheck = checkInviteEmailMismatch((event as any).invitedEmail, auth?.email, InviteType.DATE_PICKER);

  const handleSwitchAccount = (): void => {
    authenticationService.logOut();
    const loginState: LoginPageState = {
      inviteType: InviteType.DATE_PICKER,
      eventId: `${event.id}`,
      hideBackButton: true,
    };
    navigate('/login', { state: loginState });
  };

  const { deadline } = event;

  const deadlineIsPassed = deadline ? new Date() > new Date(deadline) : false;
  if (emailCheck.status === 'email-mismatch' && (event as any).invitedEmail && auth?.email) {
    return (
      <ResponsePageWrapper>
        {event && (
          <ResponsePageWrapper.SidePanel>
            <EventOverview event={event} />
          </ResponsePageWrapper.SidePanel>
        )}

        <div className="flex flex-col gap-4">
          <h2 className="text-lg font-medium">{t('page.events.invite.datePickers.authWrongAccount.title')}</h2>
          <p className="text-sm">
            {t('page.events.invite.datePickers.authWrongAccount.message', {
              inviteeEmail: (event as any).invitedEmail,
              currentEmail: auth.email,
            })}
          </p>
        </div>

        <div className="mt-6 flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
          <Button className="underline" onClick={() => navigate('/')}>
            {t('general.cancel')}
          </Button>
          <Button variant="primary" className="px-[32px]" onClick={handleSwitchAccount}>
            {t('page.events.invite.datePickers.authWrongAccount.button')}
          </Button>
        </div>
      </ResponsePageWrapper>
    );
  }

  return (
    <ResponsePageWrapper>
      {event && (
        <ResponsePageWrapper.SidePanel>
          <EventOverview event={event} />
        </ResponsePageWrapper.SidePanel>
      )}

      {!deadlineIsPassed && (
        <div className="flex flex-col gap-4">
          <h2 className="text-xl font-medium">{t('page.events.invite.datePickers.giveAvailability')}</h2>

          {event.dateOptions.map((dateOption) => {
            const userId = getUserId();
            const userAnswer = dateOption.answers.find((answer) => answer.id === userId);

            return (
              <EventSelectDateOption
                key={dateOption.id}
                dateOption={dateOption}
                eventId={+event.id}
                dateOptionAnswer={dateOptionsUpdate[dateOption.id] || userAnswer || {}} // Default to empty object if no answer
                onSave={(__, dateOptionId, answer, description) => {
                  const update = { [dateOptionId]: { answer, description } };
                  setDateOptionsUpdate((prev) => ({ ...prev, ...update }));
                }}
              />
            );
          })}

          {event && event.customFields.length > 0 && (
            <>
              <div className="text-lg">{t('page.events.invite.polls.title')}</div>
              <CustomFieldsForm
                fields={event.customFields}
                response={customFieldResponses}
                setResponse={setCustomFieldResponses}
              />
            </>
          )}

          <div className="mt-6 flex h-full min-h-10 flex-1 items-end justify-end align-bottom">
            <Button className="underline" onClick={() => navigate('/')}>
              {t('general.cancel')}
            </Button>
            <Button variant="primary" className="px-[32px]" onClick={handleSubmit}>
              {t('general.save')}
            </Button>
          </div>
        </div>
      )}
    </ResponsePageWrapper>
  );
}

export default AuthenticatedDatePickerRespondPage;
